class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('details.search_details');
    this.isOpen = false;
    /** Cancels in-flight “view all” count fetches when the query changes (stops wrong/slow updates). */
    this._viewAllCountAbort = null;
    /** Monotonic id so late responses never overwrite the UI for a newer term. */
    this._viewAllCountSeq = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 500).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    this.closeStickySearchButton?.addEventListener('click', this.onCloseStickySearchClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        this.hideTrendingAndProducts();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    // Bump cache key when predictive fetch/limit logic changes (avoids stale 3-item cached markup).
    const queryKey = `${searchTerm.replace(/ /g, '-').toLowerCase()}:ps10`;
    this.activeSearchTerm = searchTerm;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    if (this._viewAllCountAbort) {
      this._viewAllCountAbort.abort();
    }
    this._viewAllCountAbort = new AbortController();
    const countSignal = this._viewAllCountAbort.signal;
    this._viewAllCountSeq += 1;
    const seq = this._viewAllCountSeq;

    const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product&options%5Bprefix%5D=last`;
    const termForThisRequest = searchTerm;
    // Run in parallel with predictive HTML so the “(n)” count is ready as soon as the dropdown paints.
    const countPromise = this.getTotalResults(linkTotal, termForThisRequest, countSignal);

    // Shopify predictive search caps product `resources[limit]` at 10. Request the max so Liquid can
    // filter out zero-inventory items and still fill the grid (theme shows up to 3 in predictive-search.liquid).
    const predictiveProductLimit = 10;

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
        'resources[type]'
      )}=product&${encodeURIComponent('resources[limit]')}=${predictiveProductLimit}&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        if (this.activeSearchTerm !== termForThisRequest) return null;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        return countPromise;
      })
      .then((countResult) => {
        if (this.activeSearchTerm !== termForThisRequest) return;
        this._applyViewAllCount(linkTotal, countResult, termForThisRequest, seq);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('[id^="shopify-section-header"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.searchDetails?.setAttribute('open', true);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
    this.searchDetails?.removeAttribute('open', true);
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.add('is-show');
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.remove('is-show');
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    let closeButton;
    if (e.target.matches('.header-search-popup-close') || e.target.matches('.header-search-close')) {
      closeButton = e.target;
    } else if (e.target.closest('.header-search-popup-close')) {
      closeButton = e.target.closest('.header-search-popup-close');
    } else if (e.target.closest('.header-search-close')) {
      closeButton = e.target.closest('.header-search-close');
    }

    const isInModal = this.contains(e.target);
    if (isInModal && !closeButton) return;

    this.close();
    this.hideTrendingAndProducts();
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return;

    const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product&options%5Bprefix%5D=last`;
    const termForCount = searchTerm;

    if (this._viewAllCountAbort) {
      this._viewAllCountAbort.abort();
    }
    this._viewAllCountAbort = new AbortController();
    const signal = this._viewAllCountAbort.signal;

    this._viewAllCountSeq += 1;
    const seq = this._viewAllCountSeq;

    qsViewAllLink.href = linkTotal;
    return this.getTotalResults(linkTotal, termForCount, signal).then((result) => {
      this._applyViewAllCount(linkTotal, result, termForCount, seq);
    });
  }

  _applyViewAllCount(linkTotal, result, termForCount, seq) {
    if (seq !== this._viewAllCountSeq) return;
    if (this.activeSearchTerm !== termForCount) return;
    if (result == null) return;

    const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return;

    qsViewAllLink.href = linkTotal;

    const inStock = typeof result === 'object' && result !== null ? result.inStock : result;
    const zeroInv =
      typeof result === 'object' && result !== null ? result.zeroInventory : '0';
    const safeCount = inStock !== '' && inStock != null ? String(inStock) : '0';
    qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace(/\([^)]*\)/, `(${safeCount})`);

    const note = this.predictiveSearchResults.querySelector('[data-qs-inventory-note]');
    if (note) {
      const tpl = note.getAttribute('data-line-template') || '';
      const z = parseInt(String(zeroInv), 10) || 0;
      if (z > 0 && tpl) {
        note.textContent = tpl.split('[[COUNT]]').join(String(z));
        note.hidden = false;
      } else {
        note.textContent = '';
        note.hidden = true;
      }
    }
  }

  /**
   * In-stock total = every `#CollectionProductGrid #main-collection-product-grid li.product` the
   * search template actually outputs (inventory-filtered). Shopify `search.results` outside
   * `{% paginate %}` is unreliable, so `data-search-count` alone often shows wrong totals (e.g. 1).
   * We sum list rows across pages; fetch extra pages in parallel when numbered pagination exists.
   */
  getTotalResults(firstPageUrl, termForCount, signal) {
    const origin =
      typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : '';

    const baseUrl = (() => {
      try {
        return new URL(firstPageUrl, origin);
      } catch (e) {
        return null;
      }
    })();

    if (!baseUrl) {
      return Promise.resolve(null);
    }

    const pathAndSearch = `${baseUrl.pathname}${baseUrl.search}`;

    const countListedProducts = (doc) =>
      doc.querySelectorAll('#CollectionProductGrid #main-collection-product-grid li.product').length;

    const parseSection = (doc) =>
      doc.querySelector('[data-section-type="search"][data-search-count]') ||
      doc.querySelector('[id^="SearchSection"]');

    const parseNextHref = (doc) => {
      const nextArrow = doc.querySelector('a.pagination__item--next[href]');
      if (nextArrow) return nextArrow.getAttribute('href');
      const inf = doc.querySelector('a[data-infinite-scrolling][data-href]:not(.disabled)');
      if (inf) return inf.getAttribute('data-href');
      return null;
    };

    const maxNumberedPage = (doc) => {
      let m = 1;
      doc.querySelectorAll('.pagination-num a.pagination__item').forEach((a) => {
        const n = parseInt(a.textContent.trim(), 10);
        if (!Number.isNaN(n)) m = Math.max(m, n);
      });
      return m;
    };

    const fetchDoc = (urlPathSearch) => {
      if (signal.aborted) return Promise.resolve(null);
      return fetch(urlPathSearch, { signal, credentials: 'same-origin' }).then((response) => {
        if (!response.ok) {
          const err = new Error(response.status);
          this.close();
          throw err;
        }
        return response.text();
      });
    };

    return fetchDoc(pathAndSearch)
      .then((text) => {
        if (termForCount != null && this.activeSearchTerm !== termForCount) return null;
        if (signal.aborted) return null;

        const firstDoc = new DOMParser().parseFromString(text, 'text/html');
        const section = parseSection(firstDoc);
        let totalMatching = null;
        if (section && section.dataset && section.dataset.totalMatchingProducts != null) {
          const t = parseInt(section.dataset.totalMatchingProducts, 10);
          if (!Number.isNaN(t)) totalMatching = t;
        }

        let inStockTotal = countListedProducts(firstDoc);

        if (inStockTotal === 0 && totalMatching != null && totalMatching > 0) {
          return {
            inStock: '0',
            zeroInventory: String(totalMatching),
          };
        }

        const finish = (total) => {
          const zero =
            totalMatching != null ? Math.max(0, totalMatching - total) : 0;
          return { inStock: String(total), zeroInventory: String(zero) };
        };

        const fetchPagesParallel = (fromPage, toPage, runningTotal) => {
          const tasks = [];
          for (let p = fromPage; p <= toPage; p++) {
            const u = new URL(baseUrl.href);
            u.searchParams.set('page', String(p));
            tasks.push(
              fetchDoc(`${u.pathname}${u.search}`).then((html) => {
                if (termForCount != null && this.activeSearchTerm !== termForCount) return 0;
                if (!html) return 0;
                const d = new DOMParser().parseFromString(html, 'text/html');
                return countListedProducts(d);
              })
            );
          }
          return Promise.all(tasks).then((counts) => {
            if (termForCount != null && this.activeSearchTerm !== termForCount) return null;
            let t = runningTotal;
            counts.forEach((c) => {
              t += c;
            });
            return finish(t);
          });
        };

        // Server-side page count from Liquid (`paginate.pages`) — not the visible “Show more” control.
        const metaEl = firstDoc.querySelector('[data-search-pagination-meta]');
        let liquidPages = NaN;
        if (metaEl && metaEl.dataset && metaEl.dataset.pages != null) {
          const parsed = parseInt(metaEl.dataset.pages, 10);
          if (!Number.isNaN(parsed)) liquidPages = Math.min(Math.max(parsed, 1), 40);
        }

        if (!Number.isNaN(liquidPages)) {
          if (liquidPages <= 1) {
            return finish(inStockTotal);
          }
          return fetchPagesParallel(2, liquidPages, inStockTotal);
        }

        const lastPage = maxNumberedPage(firstDoc);
        const hasNumbered = firstDoc.querySelector('.pagination-num') != null;
        const hasNext = parseNextHref(firstDoc) != null;

        const sumNextSequential = (href, runningTotal, depth) => {
          if (!href || depth >= 40 || signal.aborted) {
            return Promise.resolve(finish(runningTotal));
          }
          if (termForCount != null && this.activeSearchTerm !== termForCount) {
            return Promise.resolve(null);
          }
          let path;
          try {
            const u = new URL(href, origin);
            path = `${u.pathname}${u.search}`;
          } catch (e) {
            return Promise.resolve(finish(runningTotal));
          }
          return fetchDoc(path).then((html) => {
            if (termForCount != null && this.activeSearchTerm !== termForCount) return null;
            if (!html) return finish(runningTotal);
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const n = countListedProducts(doc);
            const newTotal = runningTotal + n;
            if (n === 0) return finish(newTotal);
            const next = parseNextHref(doc);
            const nextAbs = next ? new URL(next, origin).href : null;
            return sumNextSequential(nextAbs, newTotal, depth + 1);
          });
        };

        if (!hasNext && lastPage <= 1) {
          return finish(inStockTotal);
        }

        if (hasNumbered && lastPage > 1) {
          return fetchPagesParallel(2, lastPage, inStockTotal);
        }

        return sumNextSequential(parseNextHref(firstDoc), inStockTotal, 0);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return null;
        this.close();
        throw error;
      });
  }
}

customElements.define('predictive-search', PredictiveSearch);